import React, { useEffect } from "react";
import { useTenantStore } from "./use-tenant-store";
import { Partner } from "./tenant.types";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/axios/config";
import { AnimatePresence, motion } from "framer-motion";
import { LuLoader2 } from "react-icons/lu";
import { MdErrorOutline } from "react-icons/md";
import ButtonComponent from "../../components/ButtonComponent";
import { appInsights } from "../../components/AppInsightsProvider";
import { resolverTenantUrl } from "./resolver-tenant-url";

type PartnerProviderProps = React.PropsWithChildren<{}>;

const TenantProvider = ({ children }: PartnerProviderProps) => {
  const { setPartner } = useTenantStore();
  const [retryCount, setRetryCount] = React.useState(0);

  const getTenantPartnerRequestCtx = useQuery({
    queryKey: ["tenant-partner"],
    queryFn: async () => {
      const parceiro = await api.get<Partner>("/onboarding/tenant", {
        headers: {
          TenantUrl: resolverTenantUrl(),
        },
      });
      return parceiro.data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  useEffect(() => {
    if (getTenantPartnerRequestCtx.isSuccess) {
      setPartner(getTenantPartnerRequestCtx.data);
    }

    if (getTenantPartnerRequestCtx.isError) {
      appInsights.trackException({
        exception: new Error("Erro ao buscar dados do parceiro"),
        properties: {
          retries: retryCount,
          location: window.location.href,
          hostname: window.location.hostname,
        },
      });
    }
  }, [getTenantPartnerRequestCtx.isSuccess, getTenantPartnerRequestCtx.data, getTenantPartnerRequestCtx.isError, setPartner, retryCount]);

  return (
    <AnimatePresence>
      <motion.main className="min-h-screen w-full z-[99]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {getTenantPartnerRequestCtx.isLoading && (
          <div className="flex flex-col w-full m-auto items-center justify-center gap-2 min-h-screen">
            <LuLoader2 className="animate-spin text-zinc-300" size={48} />
            <h1 className="font-bricolage-grotesque font-semibold text-xl text-zinc-700">Buscando dados</h1>
          </div>
        )}

        {getTenantPartnerRequestCtx.isError ? (
          <div className="flex flex-col w-full m-auto items-center justify-center gap-2 min-h-screen">
            <MdErrorOutline className="text-red-400" size={48} />
            <h1 className="font-bricolage-grotesque font-semibold text-xl text-zinc-700 max-w-[24ch] text-center">
              Houve um erro ao buscar dados do parceiro
            </h1>
            {retryCount < 3 && (
              <ButtonComponent
                className="bg-zinc-100 rounded-lg px-4 py-2 font-kumbh-sans font-semibold text-zinc-700 mt-4 text-sm flex items-center"
                onClick={() => {
                  setRetryCount((prev) => prev + 1);
                  getTenantPartnerRequestCtx.refetch();
                }}
              >
                Tentar novamente
              </ButtonComponent>
            )}
          </div>
        ) : null}

        {getTenantPartnerRequestCtx.isSuccess ? children : null}
      </motion.main>
    </AnimatePresence>
  );
};

export { TenantProvider };
